'use client';

import { useCppEventsHandler } from '@/hooks/use-rewards/use-cpp-events-handler';
import {
    mapPromotionToClickContext,
    storePromoNavigationSource,
    type PromotionClickContext
} from '@/hooks/use-rewards/use-rewards-promo-tracking';
import { useCppConfig } from '@/hooks/use-rewards/use-rewards';
import { usePromotionLauncher } from '@/hooks/use-promotion-launcher/use-promotion-launcher.hook';
import {
    trackRewardsHubAmplitude,
    trackRewardsHubLinkEvent,
    trackPromoCardViewedToAmplitude,
    trackPromoHubCardsAllViewedToAmplitude,
    trackPromoOptInSubmittedToAmplitude,
    trackPromoOptInFailedToAmplitude,
    trackPromoOptInSuccessfulToAmplitude,
    type RewardsHubSharedState,
    type PromoOptInContext
} from '@/tracking/amplitude/events/rewards-hub/rewards-hub';
import {
    trackRewardsHubPromoEventToAmplitude,
    PromoApiEventType,
    type RewardsHubPromoEvent
} from '@/tracking/amplitude/events/rewards-hub/promo-events';
import { trackRewardsHubPromoClickToAmplitude } from '@/tracking/amplitude/events/rewards-hub/promo-click';
import {
    trackReferWidgetClickedToAmplitude,
    trackReferWidgetViewedToAmplitude
} from '@/tracking/amplitude/events/rewards-hub/refer-widget';
import {
    REWARDS_HUB_AMP_EVENT,
    REWARDS_MODULE,
    REWARDS_LINK_TYPE,
    REWARDS_DEFAULT_LINK_TEXT,
    REWARDS_PAGE_NAME,
    LOBBY_PAGE_NAME
} from '@/tracking/amplitude/events/rewards-hub/event-names';
import { normalizeRafDestinationUrl } from '@/utils/url/normalize-raf-destination';
import { toSnakeCase } from '@/tracking/amplitude/utils';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useStore } from '@/store/store';
import { ddRumTimingOnce } from '@/utils/datadog';
import { isPromoViewedInSession, markPromoViewedInSession } from '@/utils/rewards-hub/promo-progress-viewed-session';
import { useMemo, useRef, useState } from 'react';
import {
    getBonusSpinsRewardCardTrackingEvents,
    getCasinoBonusRewardCardTrackingEvents,
    getRewardBoxTrackingEvents,
    getRewardCardTrackingEvents,
    getRewardMachineCardTrackingEvents
} from '@/tracking/amplitude/events/rewards-hub/utils';

export type RewardsHubAnalyticsSurface = 'rewards-hub' | 'merch-swimlane';

export type UseRewardsHubAnalyticsHandlersOptions = {
    /** Lobby merch swimlane (home) vs full rewards hub page (rewards). */
    surface?: RewardsHubAnalyticsSurface;
    module?: (typeof REWARDS_MODULE)[keyof typeof REWARDS_MODULE];
};

type CppEventsHandlerEvent = {
    type?: string;
    href?: string;
    uri?: string;
    promoCode?: string;
    label?: string;
    description?: string;
    promoName?: string;
    message?: string;
};

/**
 * Handlers + promoHandlers for Rewards Hub and generosity swimlane mounts.
 */
export const useRewardsHubAnalyticsHandlers = (
    isLoggedIn: boolean | undefined,
    options?: UseRewardsHubAnalyticsHandlersOptions
) => {
    const surface = options?.surface ?? 'rewards-hub';
    const surfaceRef = useRef<RewardsHubAnalyticsSurface>(surface);
    surfaceRef.current = surface;

    const sharedState = useRef<RewardsHubSharedState | null>(null);
    const [sharedStateLoaded, setSharedStateLoaded] = useState(false);
    const pendingCardViewedEvents = useRef<PromotionClickContext[]>([]);
    const pendingAllCardsViewed = useRef(false);
    const activeCardsRef = useRef(0);

    const sendEvent = useStore((s) => s.tracking.sendEvent);
    const [eventsHandler, anchorCallback] = useCppEventsHandler();
    const [promoConfig] = useCppConfig();
    const { isIosWrapper } = useGlobalContext();
    const promotionLauncher = usePromotionLauncher();

    const { handlers, trackPromoHubCardViewed } = useMemo(() => {
        const getAnalyticsBaseProps = () =>
            surfaceRef.current === 'merch-swimlane'
                ? {
                      Module: REWARDS_MODULE.MERCH_SWIM_LANE,
                      'Page Name': LOBBY_PAGE_NAME
                  }
                : {
                      Module: REWARDS_MODULE.REWARDS_ACTIVE_CARDS,
                      'Page Name': REWARDS_PAGE_NAME
                  };

        const shouldDedupePromoViewed = () => surfaceRef.current === 'merch-swimlane';

        const track = (event: string, props?: Record<string, unknown>) => {
            trackRewardsHubAmplitude({
                sendEvent,
                event,
                sharedState: sharedState.current,
                props: {
                    ...getAnalyticsBaseProps(),
                    ...props
                }
            });
        };
        const rbHandlers = getRewardBoxTrackingEvents(track, options?.module ?? REWARDS_MODULE.REWARDS_MODULE);
        const rcHandlers = getRewardCardTrackingEvents(track);
        const cbHandlers = getCasinoBonusRewardCardTrackingEvents(track);
        const bsHandlers = getBonusSpinsRewardCardTrackingEvents(track);
        const rmHandlers = getRewardMachineCardTrackingEvents(track);

        const trackLink = (args: {
            event: string;
            linkText: string;
            linkUrl?: string;
            eventLabel?: string;
            rewardAmount?: string | number;
            extra?: Record<string, unknown>;
        }) => {
            trackRewardsHubLinkEvent({
                sendEvent,
                event: args.event,
                sharedState: sharedState.current,
                linkText: args.linkText,
                linkUrl: args.linkUrl,
                eventLabel: args.eventLabel,
                rewardAmount: args.rewardAmount,
                extra: {
                    ...getAnalyticsBaseProps(),
                    ...args.extra
                }
            });
        };

        const trackPromoHubCardViewedImpl = (promotion: PromotionClickContext, state: RewardsHubSharedState) => {
            const promoId = promotion?.promoCode;
            if (!promoId) {
                return;
            }
            if (shouldDedupePromoViewed() && isPromoViewedInSession(promoId)) {
                return;
            }
            if (shouldDedupePromoViewed()) {
                markPromoViewedInSession(promoId);
            }

            const merchAnalytics = {
                pageName: LOBBY_PAGE_NAME,
                module: REWARDS_MODULE.MERCH_SWIM_LANE
            };

            trackPromoCardViewedToAmplitude({
                sendEvent,
                sharedState: state,
                context: mapPromotionToClickContext(promotion),
                analyticsContext: shouldDedupePromoViewed() ? merchAnalytics : undefined
            });
        };

        const handlersImpl = {
            ...rbHandlers,
            ...rcHandlers,
            ...cbHandlers,
            ...bsHandlers,
            ...rmHandlers,
            handleSharedState: (props: RewardsHubSharedState) => {
                sharedState.current = props;
                setSharedStateLoaded((prev) => (prev ? prev : true));

                pendingCardViewedEvents.current?.forEach((promotion) => {
                    trackPromoHubCardViewedImpl(promotion, props);
                });
                pendingCardViewedEvents.current = [];

                if (pendingAllCardsViewed.current) {
                    trackPromoHubCardsAllViewedToAmplitude({
                        sendEvent,
                        sharedState: props
                    });
                    pendingAllCardsViewed.current = false;
                }
            },

            handleActivePromoLoaded: (props: { activeCards: number }) => {
                activeCardsRef.current = props.activeCards;
            },

            handleActiveCardCTAClicked: (props: {
                promoId: string;
                promoName: string;
                linkText: string;
                promoProgress: number;
                activeCards?: number;
            }) =>
                track(REWARDS_HUB_AMP_EVENT.REWARDS_ACTIVE_CARD_CTA_CLICKED, {
                    'Promo ID': props.promoId,
                    'Promo Name': props.promoName,
                    'Link Text': toSnakeCase(props.linkText),
                    'Promo Progress': props.promoProgress,
                    'Active Cards': props.activeCards ?? activeCardsRef.current
                }),

            handleActiveCardExpanded: (props: { linkText: string }) =>
                track(REWARDS_HUB_AMP_EVENT.REWARDS_ACTIVE_CARD_EXPANDED, {
                    'Link Text': toSnakeCase(props.linkText)
                }),

            handleActiveCardCollapsed: (props: { linkText: string }) =>
                track(REWARDS_HUB_AMP_EVENT.REWARDS_ACTIVE_CARD_COLLAPSED, {
                    'Link Text': toSnakeCase(props.linkText)
                }),

            handleActiveErrorCTAClicked: (props: { linkText: string }) =>
                track(REWARDS_HUB_AMP_EVENT.REWARDS_ACTIVE_ERROR_CTA_CLICKED, {
                    'Link Text': toSnakeCase(props.linkText)
                }),

            handlePromoCardViewed: (props: {
                position: number;
                promoName: string;
                promoId: string;
                interactionType: 'screen_view' | 'scroll';
                promoProgress: number;
            }) => {
                if (shouldDedupePromoViewed() && isPromoViewedInSession(props.promoId)) {
                    return;
                }
                if (shouldDedupePromoViewed()) {
                    markPromoViewedInSession(props.promoId);
                }

                track(REWARDS_HUB_AMP_EVENT.PROMO_PROGRESS_CARD_VIEWED, {
                    Position: props.position,
                    'Promo ID': props.promoId,
                    'Promo Name': props.promoName,
                    'Interaction Type': props.interactionType,
                    'Promo Progress': props.promoProgress
                });
            },

            handleNavigationLinkClicked: (props: { linkText: string; linkUrl: string }) =>
                track(REWARDS_HUB_AMP_EVENT.NAVIGATION_LINK_CLICKED, {
                    'Link Text': toSnakeCase(props.linkText),
                    'Link URL': props.linkUrl
                }),

            handleRewardsOnboardingError: (props: { errorReason: string }) =>
                track(REWARDS_HUB_AMP_EVENT.ONBOARDING_ERROR, props),
            handleRewardsOnboardingCtaClick: (props: { linkText: string; textHeader: string }) =>
                trackLink({
                    event: REWARDS_HUB_AMP_EVENT.ONBOARDING_CTA_CLICKED,
                    linkText: props.linkText,
                    extra: { 'Text Header': props.textHeader }
                }),
            handleRewardsOnboardingModalView: (props: { textHeader: string; paginationNumber: number }) =>
                track(REWARDS_HUB_AMP_EVENT.ONBOARDING_VIEWED, {
                    'Text Header': props.textHeader,
                    'Pagination Number': props.paginationNumber
                }),

            handlePrimaryPerkTooltipClick: () => track(REWARDS_HUB_AMP_EVENT.PRIMARY_PERK_TOOLTIP_CLICKED),
            handlePrimaryPerkCtaClick: (props: { linkText: string; rewardAmount: number; linkUrl: string }) =>
                trackLink({
                    event: REWARDS_HUB_AMP_EVENT.PRIMARY_PERK_CTA_CLICKED,
                    linkText: props.linkText,
                    linkUrl: props.linkUrl,
                    rewardAmount: props.rewardAmount
                }),

            handleLoggedOutUserCasinoBonusClick: (props: { linkText: string }) =>
                trackLink({
                    event: REWARDS_HUB_AMP_EVENT.LOGGED_OUT_CASINO_BONUS_CLICKED,
                    linkText: props.linkText
                }),
            handleLoggedInUserCasinoBonusClick: (props: { linkText: string }) =>
                trackLink({
                    event: REWARDS_HUB_AMP_EVENT.LOGGED_IN_CASINO_BONUS_CLICKED,
                    linkText: props.linkText
                }),

            handleHowItWorksView: () => track(REWARDS_HUB_AMP_EVENT.HOW_IT_WORKS_VIEWED),
            handleLoyaltyToggle: (props: { expanded: boolean }) => {
                if (!props.expanded) return;
                track(REWARDS_HUB_AMP_EVENT.LOYALTY_PROGRESS_BAR_OPENED, { Module: REWARDS_MODULE.REWARDS_HUB });
            },

            handlePromotionTabView: (props: { location?: string }) =>
                track(REWARDS_HUB_AMP_EVENT.PROMOTION_TAB_VIEWED, {
                    ...(props?.location ? { Tab: props.location } : {})
                }),
            handleInfoModalViewClick: (props: { interactionType: string }) =>
                track(REWARDS_HUB_AMP_EVENT.BONUS_SPINS_INFO_MODAL_VIEW_CLICKED, props),
            handleModuleClick: (props: {
                rewardAmount: string;
                linkText: string;
                linkUrl: string;
                eventLabel?: string;
            }) =>
                trackLink({
                    event: REWARDS_HUB_AMP_EVENT.BONUS_SPINS_MODULE_CLICKED,
                    linkText: props.linkText,
                    linkUrl: props.linkUrl,
                    eventLabel: props.eventLabel,
                    rewardAmount: props.rewardAmount
                }),
            handleBonusSpinsModalCtaClick: (props: {
                rewardAmount: string;
                linkText: string;
                eventLabel?: string;
                linkUrl: string;
            }) =>
                trackLink({
                    event: REWARDS_HUB_AMP_EVENT.BONUS_SPINS_MODAL_CTA_CLICKED,
                    linkText: props.linkText,
                    linkUrl: props.linkUrl,
                    eventLabel: props.eventLabel,
                    rewardAmount: props.rewardAmount
                }),
            handleComponentTiming: ddRumTimingOnce,

            handleReferClick: ({ destinationURL }: { destinationURL: string }) => {
                const normalized = normalizeRafDestinationUrl(destinationURL);
                if (!normalized) return;
                trackReferWidgetClickedToAmplitude({
                    sendEvent,
                    sharedState: sharedState.current
                });
                window.location.href = normalized;
            },
            handleReferWidgetView: () => {
                trackReferWidgetViewedToAmplitude({
                    sendEvent,
                    sharedState: sharedState.current
                });
            },

            handlePromoOptInSubmitted: (props: PromoOptInContext) =>
                trackPromoOptInSubmittedToAmplitude({
                    sendEvent,
                    sharedState: sharedState.current,
                    context: props
                }),
            handlePromoOptInFailed: (props: PromoOptInContext) =>
                trackPromoOptInFailedToAmplitude({
                    sendEvent,
                    sharedState: sharedState.current,
                    context: props
                }),
            handlePromoOptInSuccessful: (props: PromoOptInContext) =>
                trackPromoOptInSuccessfulToAmplitude({
                    sendEvent,
                    sharedState: sharedState.current,
                    context: props
                })
        };

        return { handlers: handlersImpl, trackPromoHubCardViewed: trackPromoHubCardViewedImpl };
    }, [sendEvent]);

    const promoHandlers = useMemo(
        () => ({
            ...promoConfig,
            isLoggedIn,
            onVisibleCardCallback: (promotion: PromotionClickContext) => {
                if (!sharedState.current) {
                    pendingCardViewedEvents.current?.push(promotion);
                    return;
                }

                trackPromoHubCardViewed(promotion, sharedState.current);
            },
            onPrimaryLinkClicked: (promotion: PromotionClickContext, innerText: string | undefined) => {
                trackRewardsHubPromoClickToAmplitude({
                    sendEvent,
                    sharedState: sharedState.current,
                    type: REWARDS_LINK_TYPE.PRIMARY,
                    linkText: innerText ?? REWARDS_DEFAULT_LINK_TEXT.PRIMARY_CTA,
                    context: mapPromotionToClickContext(promotion)
                });
            },
            onSecondaryLinkClicked: (promotion: PromotionClickContext, innerText: string | undefined) => {
                storePromoNavigationSource(promotion?.location);
                trackRewardsHubPromoClickToAmplitude({
                    sendEvent,
                    sharedState: sharedState.current,
                    type: REWARDS_LINK_TYPE.SECONDARY,
                    linkText: innerText ?? REWARDS_DEFAULT_LINK_TEXT.SECONDARY_CTA,
                    context: mapPromotionToClickContext(promotion)
                });
            },
            onCardClicked: (promotion: PromotionClickContext, imageUrl: string) => {
                storePromoNavigationSource(promotion?.location);
                trackRewardsHubPromoClickToAmplitude({
                    sendEvent,
                    sharedState: sharedState.current,
                    type: REWARDS_LINK_TYPE.IMAGE,
                    linkText: imageUrl,
                    context: mapPromotionToClickContext(promotion)
                });
            },
            onAllCardsViewed: () => {
                if (!sharedState.current) {
                    pendingAllCardsViewed.current = true;
                    return;
                }
                trackPromoHubCardsAllViewedToAmplitude({
                    sendEvent,
                    sharedState: sharedState.current
                });
            },
            events: (event: unknown) => {
                const eventType = (event as { type?: string })?.type;

                // On rewards-hub, opt-in API events are handled by handlePromoOptIn* — skip to avoid duplicates.
                if (!Object.values(PromoApiEventType).includes(eventType as PromoApiEventType)) {
                    trackRewardsHubPromoEventToAmplitude({
                        sendEvent,
                        event: event as RewardsHubPromoEvent,
                        sharedState: sharedState.current
                    });
                }
                eventsHandler?.(event as CppEventsHandlerEvent);
            },
            anchorCallback,
            wrappedApplication: isIosWrapper,
            launcher: promotionLauncher
        }),
        [
            promoConfig,
            isLoggedIn,
            anchorCallback,
            isIosWrapper,
            promotionLauncher,
            trackPromoHubCardViewed,
            sendEvent,
            eventsHandler
        ]
    );

    return { handlers, promoHandlers, sharedStateLoaded, activeCardsRef, sharedStateRef: sharedState };
};
